import { useContext } from 'react';

import { useIsCurrentUserFleetOpsStaff } from 'components/Common/FleetOpsStaffOnly';
import FeatureGatesContext from 'contexts/FeatureGatesContext';

// Staff can ask for one renderer whatever the account's gate or the rollout
// stage says (for side by side QA): localStorage dataTableRenderer = 'v2' or
// 'ag'
export const DATA_TABLE_RENDERER_STORAGE_KEY = 'dataTableRenderer';

// One account gate (dataTable) for the whole migration, turned on once for
// each pilot account. Who sees DataTable for each surface (the tables moving
// from AG Grid) on an account with the gate on:
// - staff: FleetOps staff only
// - gated: everyone on the account (the pilots)
// - all: everyone everywhere, gate or not (the AG path is about to go)
// A surface moves along by a one-line change here once its stage is QA'd,
// so pilots pick up each phase without their gate changing.
export const ROLLOUT: { [surface in DataTable.Surface]: DataTable.Rollout } = {
  v5Matrix: 'staff',
  simpleGrid: 'staff',
  paceMatrix: 'staff',
  rankingMatrix: 'staff',
  singleMetricDateMatrix: 'staff',
  goalKpis: 'staff',
  insightsGrid: 'staff',
  search: 'staff',
  popupGrid: 'staff',
  orderTab: 'staff',
  board: 'staff',
  customerLaneBoard: 'staff',
  laneCommitConfigs: 'staff',
  performanceBoard: 'staff',
  driverSetup: 'staff',
  recordTagging: 'staff',
  exploreRawData: 'staff',
  exploreDimensions: 'staff',
};

const readRendererOverride = (): 'v2' | 'ag' | undefined => {
  try {
    const renderer = window.localStorage.getItem(
      DATA_TABLE_RENDERER_STORAGE_KEY,
    );
    return renderer === 'v2' || renderer === 'ag' ? renderer : undefined;
  } catch (ex) {
    return undefined;
  }
};

export const isDataTableOn = ({
  rollout,
  isGateOn,
  isStaff,
}: {
  rollout: DataTable.Rollout;
  isGateOn: boolean;
  isStaff: boolean;
}) => {
  if (rollout === 'all') {
    return true;
  }
  if (rollout === 'gated') {
    return isGateOn;
  }
  return isGateOn && isStaff;
};

// Whether a surface draws with DataTable instead of AG Grid. With
// isOnForGatedAccount, a gated account draws it with DataTable for everyone,
// whatever the surface's stage: for content using something only DataTable
// can draw (a matrix's stacked secondary rows), set up on a gated account.
// A staff member's own renderer choice still wins.
const useIsDataTable = (
  surface: DataTable.Surface,
  { isOnForGatedAccount }: { isOnForGatedAccount?: boolean } = {},
): boolean => {
  const { isDataTableEnabled } = useContext(FeatureGatesContext);
  const isStaff = !!useIsCurrentUserFleetOpsStaff();

  if (isStaff) {
    const override = readRendererOverride();
    if (override) {
      return override === 'v2';
    }
  }

  if (isOnForGatedAccount && isDataTableEnabled) {
    return true;
  }

  return isDataTableOn({
    rollout: ROLLOUT[surface],
    isGateOn: isDataTableEnabled,
    isStaff,
  });
};

export default useIsDataTable;
